/**
 * Rewards → "Shop & earn Miles": card-linked Miles at a glance. Pending and
 * earned Miles, the participating merchants (Fidel offers), and recent
 * activity. Linking happens here too; managing cards lives on Profile.
 */
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { CreditCard, Plus, Loader2, Store } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { REWARDS_SECTION_CARD, BRAND_PRIMARY_BTN_CLASS } from '@/constants/brandColors';
import { useCardLinkedMiles } from '@/hooks/useCardLinkedMiles';

const STATUS = {
  pending: { label: 'Pending', cls: 'border-amber-400/30 bg-amber-400/10 text-amber-200' },
  credited: { label: 'Earned', cls: 'border-emerald-400/30 bg-emerald-400/10 text-emerald-200' },
  void: { label: 'Didn’t qualify', cls: 'border-white/15 bg-white/[0.04] text-white/55' },
};

export function offerLabel(o) {
  if (o.type === 'amount') return `$${o.value % 1 ? o.value.toFixed(2) : o.value} back in Miles`;
  return `${o.value}% back in Miles`;
}

function money(cents) {
  return (Math.abs(cents) / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function shortDate(iso) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const DAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const NETWORK = { visa: 'Visa', mastercard: 'Mastercard', amex: 'Amex' };

/** Every condition Fidel applies to an offer, in plain words (the Terms promise each offer states them). */
export function offerConditions(o) {
  const out = [];
  if (o.minSpendCents) out.push(`${money(o.minSpendCents)} minimum`);
  if (o.maxRewardCents) out.push(`up to ${money(o.maxRewardCents)} back`);
  if (o.maxSpendCents) out.push(`purchases up to ${money(o.maxSpendCents)}`);
  if (o.channel === 'online') out.push('online only');
  if (o.channel === 'in-store') out.push('in store only');
  if (o.days?.length) {
    const key = o.days.join(',');
    out.push(key === '1,2,3,4,5' ? 'weekdays only' : key === '0,6' ? 'weekends only' : `${o.days.map((d) => DAY[d]).join(', ')} only`);
  }
  if (o.schemes?.length) out.push(`${o.schemes.map((s) => NETWORK[s] || s).join(' and ')} cards only`);
  if (o.newCustomers) out.push(o.newCustomers.days ? `only if you haven’t shopped there in ${o.newCustomers.days} days` : 'new customers only');
  if (o.returnDays) out.push(`Miles after a ${o.returnDays}-day return window`);
  if (o.endsAt && shortDate(o.endsAt)) out.push(`ends ${shortDate(o.endsAt)}`);
  if (out.length) out[0] = out[0].charAt(0).toUpperCase() + out[0].slice(1);
  return out;
}

export function ShopAndEarnView({ loading, cards, pendingMiles, earnedMiles, activity, offers, linking, error, onLink }) {
  return (
    <div className={REWARDS_SECTION_CARD}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-5 sm:px-6 py-5 border-b border-white/[0.06]">
        <div>
          <h2 className="text-white text-[18px] font-medium tracking-tight">Shop &amp; earn Miles</h2>
          <p className="text-white/55 text-[13px] font-normal mt-0.5">Link the card you already use. Shop at participating merchants and earn Miles toward your next trip.</p>
        </div>
        <Button type="button" onClick={onLink} disabled={linking} className={cn('rounded-full h-10 px-5 text-[13px] self-start sm:self-auto', BRAND_PRIMARY_BTN_CLASS)}>
          {linking ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1.5" /> Link a card</>}
        </Button>
      </div>

      <div className="p-5 sm:p-6 space-y-6">
        {error && <p role="alert" className="rounded-xl border border-red-400/25 bg-red-500/10 px-3.5 py-2.5 text-[13px] text-red-200">{error}</p>}
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-white/40" /></div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3">
              {[
                ['Pending', pendingMiles.toLocaleString('en-US')],
                ['Earned', earnedMiles.toLocaleString('en-US')],
                ['Linked cards', String(cards.length)],
              ].map(([label, value]) => (
                <div key={label} className="rounded-2xl border border-brand-800/40 bg-brand-900/20 px-4 py-3">
                  <p className="text-white/45 text-[11px] font-normal uppercase tracking-wide">{label}</p>
                  <p className="text-white text-[20px] font-medium mt-1">{value}</p>
                </div>
              ))}
            </div>

            <section aria-labelledby="participating">
              <h3 id="participating" className="text-white text-[15px] font-medium">Participating merchants</h3>
              {offers.length > 0 ? (
                <ul className="mt-3 grid gap-3 sm:grid-cols-2">
                  {offers.map((o) => {
                    const conditions = offerConditions(o);
                    return (
                      <li key={o.id} className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
                        {o.logoUrl
                          ? <img src={o.logoUrl} alt="" className="h-9 w-9 flex-shrink-0 rounded-full bg-white object-contain" loading="lazy" />
                          : <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-white/[0.06]"><Store className="h-4 w-4 text-white/50" /></span>}
                        <div className="min-w-0">
                          <p className="text-white text-sm font-normal truncate">{o.merchant}</p>
                          <p className="text-emerald-200/90 text-[12px] font-normal">{offerLabel(o)}</p>
                          {conditions.length > 0 && <p className="mt-0.5 text-white/50 text-[11.5px] font-normal leading-snug">{conditions.join(' · ')}</p>}
                          {o.terms && <p className="mt-1 text-white/40 text-[11px] font-normal leading-snug">{o.terms}</p>}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="mt-2 text-white/55 text-[13px] font-normal">No participating merchants right now. Your linked cards stay ready, and you’ll earn as soon as merchants join.</p>
              )}
            </section>

            <section aria-labelledby="shopping-activity">
              <h3 id="shopping-activity" className="text-white text-[15px] font-medium">Recent activity</h3>
              {activity.length > 0 ? (
                <ul className="mt-3 divide-y divide-white/[0.06]">
                  {activity.map((a) => {
                    const st = STATUS[a.status] || STATUS.pending;
                    return (
                      <li key={a.id} className="flex items-center justify-between gap-3 py-3">
                        <div className="min-w-0">
                          <p className="text-white text-sm font-normal truncate">{a.kind === 'refund' ? `Refund · ${a.merchant}` : a.merchant}</p>
                          <p className="text-white/40 text-[11px] font-normal">
                            {shortDate(a.at)} · {money(a.amountCents)}{a.availableOn && shortDate(a.availableOn) ? ` · Miles on ${shortDate(a.availableOn)}` : ''}{a.test ? ' · test' : ''}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className={cn('rounded-full border px-2 py-0.5 text-[11px] font-medium', st.cls)}>{st.label}</span>
                          <span className={cn('text-sm font-medium tabular-nums', a.miles < 0 ? 'text-red-300' : 'text-white')}>
                            {a.miles > 0 ? '+' : ''}{a.miles.toLocaleString('en-US')} Miles
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="mt-2 text-white/55 text-[13px] font-normal">
                  {cards.length > 0 ? 'Shop with a linked card at a participating merchant and your Miles show up here.' : 'Link a card to start earning.'}
                </p>
              )}
            </section>

            <p className="flex items-start gap-2 text-white/40 text-[11.5px] font-normal leading-relaxed">
              <CreditCard className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
              <span>Miles show as pending until the purchase clears, usually in 2–3 days. If the offer has a return window, they’re added once it closes. A refund reduces them or takes them back. Manage your cards in <Link to="/profile" className="underline decoration-white/25 underline-offset-2 hover:text-white">Profile → Linked cards</Link>.</span>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
ShopAndEarnView.propTypes = {
  loading: PropTypes.bool, cards: PropTypes.array, pendingMiles: PropTypes.number, earnedMiles: PropTypes.number,
  activity: PropTypes.array, offers: PropTypes.array, linking: PropTypes.bool, error: PropTypes.string, onLink: PropTypes.func,
};

export default function ShopAndEarnSection() {
  const s = useCardLinkedMiles({ withOffers: true });
  return <ShopAndEarnView {...s} onLink={s.linkCard} />;
}
